/* eslint-disable react/jsx-no-undef */
import React, { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import PropTypes from 'prop-types'
import {
	Row,
	Col,
	Input,
	Checkbox,
	Button,
	ConfigProvider,
	Select,
	DatePicker
} from 'antd'
import { DownOutlined, UpOutlined } from '@ant-design/icons'
import { BrowserView, MobileView } from 'react-device-detect'
import { isEmpty } from 'lodash'
import dayjs from 'dayjs'

import OptionsSelector from './OptionsSelector'
import AllowedCategories from './AllowedCategories'
import { useStores } from '../../cls/stores/MobXProvider'
import { actLoadUsers } from '../../stores/ui-lst-users/actions'
import { actLoadBudgetList } from '../../stores/ui-lst-budget/actions'
import { translate, useSmartTranslation } from '../../cls/TranslationContext'

const { Search } = Input
/**
 * Default list of currency options for filtering or selecting currency.
 * Each option includes a unique key, label (translated), and value.
 * @type {Array<{key: string, label: string, value: string}>}
 */
export const defaultCurrencyOptions = [
	{
		key: 'by-currency',
		label: translate('__select.item%_currency'),
		value: ''
	},
	{
		key: 'by-currency-usd',
		label: 'USD',
		value: 'usd'
	},
	{
		key: 'by-currency-eur',
		label: 'Eur',
		value: 'eur'
	},
	{
		key: 'by-currency-ron',
		label: 'Ron',
		value: 'ron'
	}
]

// Default filter options for filtering items by their progress status.
/**
 * Default filter options for item progress statuses.
 * @type {Array<{key: string, value: string, label: string}>}
 */
export const defaultFilterOptions = [
	{
		key: 'by-progress',
		value: '',
		label: translate('__select.item%single.status')
	},
	{
		key: 'by-progress-all',
		value: '',
		label: translate('single.filter.options-default')
	},
	{
		key: 'by-progress-canceled',
		value: 'canceled',
		label: translate('single.canceled')
	},
	{
		key: 'by-progress-draft',
		value: 'draft',
		label: translate('single.draft')
	},
	{
		key: 'by-progress-in_review',
		value: 'in_review',
		label: translate('single.in-review')
	},
	{
		key: 'by-progress-active',
		value: 'active',
		label: translate('single.active')
	},
	{
		key: 'by-progress-pending',
		value: 'pending',
		label: translate('single.pending')
	},
	{
		key: 'by-progress-completed',
		value: 'completed',
		label: translate('single.completed')
	}
]

// Default priority options for sorting items by their priority level.
/**
 * Default priority options for sorting items.
 * @type {Array<{key: string, value: number|string, label: string}>}
 */
export const defaultPriorityOptions = [
	{
		key: 'by-priority',
		value: '',
		label: translate('__select.item%single.priority')
	},
	{
		key: 'by-priority-0',
		label: translate('single.low'),
		value: '0'
	},
	{
		key: 'by-priority-1',
		label: translate('single.medium'),
		value: '1'
	},
	{
		key: 'by-priority-2',
		label: translate('single.high'),
		value: '2'
	},
	{
		key: 'by-priority-3',
		label: translate('single.urgent'),
		value: '3'
	}
]

// Default order by options for sorting items based on various criteria.
/**
 * Default "order by" options for sorting list items by various fields.
 * @type {Array<{key: string, value: string, label: string}>}
 */
export const defaultOrderByOptions = [
	{
		key: 'by-orderBy',
		value: '',
		label: translate('single.order-by')
	},
	{
		key: 'by-orderBy-id',
		label: 'id',
		value: 'id'
	},
	{
		key: 'by-orderBy-category_id',
		label: translate('single.category-id'),
		value: 'category_id'
	},
	{
		key: 'by-orderBy-name',
		label: translate('single.name'),
		value: 'name'
	},
	{
		key: 'by-orderBy-to_delete',
		label: translate('single.date-for-deletion'),
		value: 'to_delete'
	},
	{
		key: 'by-orderBy-modified',
		label: translate('single.modified'),
		value: 'modified'
	},
	{
		key: 'by-orderBy-priority',
		label: translate('single.priority'),
		value: 'priority'
	},
	{
		key: 'by-orderBy-progress',
		label: translate('single.progress'),
		value: 'progress'
	}
]

/**
 * Default entity table options for notification filtering.
 * @type {Array<{key: string, value: string, label: string}>}
 */
export const defaultEntityTableOptions = [
	{
		key: 'by-entityType',
		value: '',
		label: translate('__select.item%single.entity-type')
	},
	{
		key: 'by-entityType-sprints',
		label: translate('_sprint.multiple'),
		value: 'sprints'
	},
	{
		key: 'by-entityType-sprints_team',
		label: translate('_sprint.team'),
		value: 'sprints_team'
	},
	{
		key: 'by-entityType-requests',
		label: translate('single.requests'),
		value: 'requests'
	}
]

/**
 * Default kind options for notification filtering.
 * @type {Array<{key: string, value: string, label: string}>}
 */
export const defaultKindTableOptions = [
	{
		key: 'by-kind',
		value: '',
		label: translate('__select.item%single.kind')
	},
	{
		key: 'by-kind-new',
		label: translate('single.new'),
		value: 'new'
	},
	{
		key: 'by-kind-update',
		label: translate('single.update'),
		value: 'update'
	},
	{
		key: 'by-kind-progress',
		label: translate('single.progress'),
		value: 'progress'
	}
]

/**
 * Default application type options for filtering.
 * @type {Array<{key: string, value: string, label: string}>}
 */
export const defaultAppTypeOptions = [
	{
		key: 'by-app-type',
		value: '',
		label: translate('__select.item%single.app-type.label')
	},
	{
		key: 'by-kind-team',
		label: translate('single.team.label'),
		value: 'team'
	},
	{
		key: 'by-kind-client',
		label: translate('_client'),
		value: 'client'
	}
]

/*
 * Filter
 * @description Filter component for filtering list items.
 * @param {object} uiStore - The MobX store for the UI.
 * @param {function} addNew - The function to add a new item.
 * @param {array} progressOptions - The progress options.
 * @param {array} hide - ['search', 'progress', 'category', 'deleted', 'addNew']
 */
const Filter = ({
	uiStore,
	addNew,
	progressOptions = [],
	priorityOptions = [],
	userOptions = [],
	orderByOptions = [],
	entityTableOptions = [],
	currencyOptions = [],
	kindOptions = [],
	appTypeOptions = [],
	hide = [],
	isForNotification = false,
	isForDocumentation = false,
	isForUser = false,
	isForBudget = false,
	module = ''
}) => {
	const { t } = useSmartTranslation()
	const { users, userAllowedCategories } = useStores()
	const { loggedInUserId, loggedInUser } = users
	const isSuperAdmin = loggedInUser.isSuperAdmin

	const _entityTableOptions =
		entityTableOptions.length > 0
			? entityTableOptions
			: defaultEntityTableOptions

	const _currencyOptions =
		currencyOptions.length > 0 ? currencyOptions : defaultCurrencyOptions

	const _appTypeOptions =
		appTypeOptions.length > 0 ? appTypeOptions : defaultAppTypeOptions

	const _kindOptions =
		kindOptions.length > 0 ? kindOptions : defaultKindTableOptions

	// progress options: enum('canceled','draft','in_review','active','pending','completed')
	const _progressOptions =
		progressOptions.length > 0 ? progressOptions : defaultFilterOptions

	// priority options: enum('0','1','2','3') represents - low, medium, high and urgent
	const _priorityOptions =
		priorityOptions.length > 0 ? priorityOptions : defaultPriorityOptions

	// progress options: enum('id','draft','name','to_delete','modified', 'priority', 'progress')
	const _orderByOptions =
		orderByOptions.length > 0 ? orderByOptions : defaultOrderByOptions

	// Generates user options based on the provided userOptions array
	// or defaults to the logged-in user's clients and team if no options are available.
	const _userOptions =
		userOptions.length > 0
			? userOptions
			: (userOptions = [
					...new Set([
						...[loggedInUserId],
						...loggedInUser.clientAndTeamIds
					])
			  ]
					.filter((n) => n)
					.map((uid) => {
						const user = users.requireUserById(uid)
						return {
							key: uid,
							label: user.name,
							value: uid
						}
					}))

	const filterValue = uiStore.filter

	// Initialize default filter value for progress
	const [defaultFilterValuesForProgress] = React.useState(
		filterValue.progress
	) // wrong, should use default from store. This will set from preferences if exists and reset wrong.

	// load users
	useEffect(() => {
		actLoadUsers({ show_all: true }).catch(console.error)
	}, [loggedInUserId])

	const handlePeriodStartChange = (date) => {
		uiStore.setFilter('period_start', date ? date.format('YYYY-MM-DD') : '')
		actLoadBudgetList().catch(console.error)
	}

	const handlePeriodEndChange = (date) => {
		uiStore.setFilter('period_end', date ? date.format('YYYY-MM-DD') : '')
		actLoadBudgetList().catch(console.error)
	}

	const modulesWithOutCategories = ['requests']
	const categoriesCheck = modulesWithOutCategories.includes(module)
		? true
		: !isEmpty(userAllowedCategories)
	return (
		<ConfigProvider
			theme={{
				token: {
					colorPrimary: '#1b1d1f'
				},
				components: {
					Input: {
						colorTextPlaceholder: '#1b1d1f'
					},
					Select: {
						optionSelectedColor: '#fff'
					}
				}
			}}
		>
			{/* Display filter for larger devices (desktop and tablets) */}
			<BrowserView>
				<Row
					className="we-filter"
					id="target-filter"
					align="middle"
					display="flex"
					gutter={[10, 10]}
					style={{
						width: '100%',
						borderRadius: 4
					}}
				>
					{/* First Row */}
					<Col id="target-filter-search">
						{!hide.includes('search') && (
							<Search
								defaultValue={uiStore.search || ''}
								placeholder={t('single.search.label')}
								onSearch={(val) => uiStore.setSearch(val)}
								enterButton
								allowClear
							/>
						)}
					</Col>
					<Col id="target-filter-progress">
						{!hide.includes('progress') && (
							<OptionsSelector
								placeholder={t('single.filter.options-default')}
								value={filterValue.progress}
								onChange={(val) => {
									// If the value is undefined (e.g., when the user clears the selection),
									// set the default filter value for 'progress'
									// Reference: https://github.com/ant-design/ant-design/issues/41277
									if (val === '') {
										uiStore.setFilter(
											'progress',
											defaultFilterValuesForProgress
										)
									} else {
										uiStore.setFilter('progress', val)
									}
								}}
								options={_progressOptions}
							/>
						)}
					</Col>

					{/* Filters only for documentation starts*/}
					{isForDocumentation && (
						<Col id="target-filter-app-type">
							{!hide.includes('appType') && (
								<OptionsSelector
									value={filterValue?.appType || ''}
									onChange={(val) =>
										uiStore.setFilter('appType', val)
									}
									options={_appTypeOptions}
								/>
							)}
						</Col>
					)}
					{/* Filters only for documentation ends*/}

					{/* Filters only for budget starts*/}
					{isForBudget && (
						<>
							<Col id="target-filter-currency">
								{!hide.includes('currency') && (
									<OptionsSelector
										dropdownMinWidth="200px"
										value={filterValue?.currency || ''}
										onChange={(val) =>
											uiStore.setFilter('currency', val)
										}
										options={_currencyOptions}
									/>
								)}
							</Col>
							<Col id="target-filter-for-user">
								<OptionsSelector
									value={filterValue?.assigned_user_id || ''}
									onChange={(val) =>
										uiStore.setFilter(
											'assigned_user_id',
											val
										)
									}
									options={[
										{
											key: 'assigned-user',
											value: '',
											label: translate('__assigned%_user')
										},
										..._userOptions
									]}
								/>
							</Col>
						</>
					)}
					{isForBudget && (
						<Row style={{ gap: '8px' }}>
							<Row span={24} id="target-filter-period-start">
								<DatePicker
									format="YYYY-MM-DD"
									value={
										filterValue?.period_start
											? dayjs(filterValue.period_start)
											: null
									}
									placeholder={translate(
										'__select.item%single.start-date'
									)}
									onChange={handlePeriodStartChange}
									allowClear
								/>
							</Row>
							<Row span={24} id="target-filter-period-end">
								<DatePicker
									format="YYYY-MM-DD"
									value={
										filterValue?.period_end
											? dayjs(filterValue.period_end)
											: null
									}
									placeholder={translate(
										'__select.item%single.end-date'
									)}
									onChange={handlePeriodEndChange}
									allowClear
								/>
							</Row>
						</Row>
					)}
					{/* Filters only for budget ends*/}

					{/* Filters only for user starts*/}
					{isForUser && (
						<>
							<Col id="target-filter-show-all">
								{!hide.includes('show_all') && isSuperAdmin && (
									<Checkbox
										checked={filterValue?.show_all || false}
										onChange={(e) =>
											uiStore.setFilter(
												'show_all',
												e.target.checked
											)
										}
									>
										{translate('single.show-all')}
									</Checkbox>
								)}
							</Col>
							<Col id="target-filter-deleted">
								{!hide.includes('deleted') && (
									<Checkbox
										checked={uiStore?.del || false}
										onChange={(e) =>
											uiStore.setDel(e.target.checked)
										}
									>
										{translate('single.deleted')}
									</Checkbox>
								)}
							</Col>
						</>
					)}
					{/* Filters only for user ends*/}

					{/* Filters only for notification starts*/}
					{isForNotification && (
						<>
							<Col id="target-filter-entity-table">
								{!hide.includes('entity_table') && (
									<OptionsSelector
										value={filterValue?.entity_table || ''}
										onChange={(val) =>
											uiStore.setFilter(
												'entity_table',
												val
											)
										}
										options={_entityTableOptions}
									/>
								)}
							</Col>
							<Col id="target-filter-kind">
								{!hide.includes('kind') && (
									<OptionsSelector
										value={filterValue?.kind || ''}
										onChange={(val) =>
											uiStore.setFilter('kind', val)
										}
										options={_kindOptions}
									/>
								)}
							</Col>
							<Col id="target-filter-show-all">
								{!hide.includes('show_all') && isSuperAdmin && (
									<Checkbox
										checked={filterValue?.show_all || false}
										onChange={(e) =>
											uiStore.setFilter(
												'show_all',
												e.target.checked
											)
										}
									>
										{translate('single.show-all')}
									</Checkbox>
								)}
							</Col>
						</>
					)}
					{/* Filters only for notification ends*/}

					<Col id="target-filter-priority">
						{!hide.includes('priority') && (
							<OptionsSelector
								value={filterValue?.priority || ''}
								onChange={(val) =>
									uiStore.setFilter('priority', val || '')
								}
								options={_priorityOptions}
							/>
						)}
					</Col>
					<Col flex="auto" id="target-filter-category">
						{!hide.includes('category') && (
							<AllowedCategories
								value={filterValue?.category || ''}
								onChange={(val) =>
									uiStore.setFilter('category', val)
								}
								optionStart={{
									key: 'by-category',
									value: '',
									label: translate('__select.item%_category')
								}}
							/>
						)}
					</Col>
					{/** todo: check this */}

					{addNew && !hide.includes('addNew') && categoriesCheck && (
						<Col flex="end">
							<Button id="target-filter-addnew" onClick={addNew}>
								{t('__add')}
							</Button>
						</Col>
					)}

					{/* Expand/Collapse Button (only show when filter is not for notification)*/}
					{!isForNotification &&
						!isForUser &&
						!hide.includes('toggleBtn') && (
							<Col flex="end" id="target-filter-toggle-btn">
								<Button
									onClick={() => uiStore.setFilterExpanded()}
								>
									{uiStore.filterExpanded ? (
										<span>
											<UpOutlined />
										</span>
									) : (
										<span>
											<DownOutlined />
										</span>
									)}
								</Button>
							</Col>
						)}

					{/* Second Row - Hidden by Default */}
					<div
						className={`filter-advanced ${
							uiStore.filterExpanded ? 'visible' : 'hidden'
						}`}
					>
						{!hide.includes('by-user') && (
							<Col flex="auto" id="target-filter-user">
								<OptionsSelector
									value={filterValue?.user_uid || ''}
									onChange={(val) =>
										uiStore.setFilter('user_uid', val)
									}
									options={[
										{
											key: 'by-user',
											value: '',
											label: translate(
												'__select.item%_user.single'
											)
										},
										..._userOptions
									]}
								/>
							</Col>
						)}

						{!hide.includes('for-user') && (
							<Col flex="auto" id="target-filter-for-user">
								<OptionsSelector
									value={filterValue?.for_user_uid || ''}
									onChange={(val) =>
										uiStore.setFilter('for_user_uid', val)
									}
									options={[
										{
											key: 'for-user',
											value: '',
											label: translate(
												'single.for-user.label'
											)
										},
										..._userOptions
									]}
								/>
							</Col>
						)}
						{!hide.includes('orderBy') && (
							<Col flex="auto" id="target-filter-order-by">
								<FilterOrderBy
									dropdownMinWidth="250px"
									value={filterValue?.orderBy || ''}
									orderDir={filterValue?.orderDir || ''}
									onChange={(val) =>
										uiStore.setFilter('orderBy', val)
									}
									onOrderDirChange={(val) =>
										uiStore.setFilter(
											'orderDir',
											val === filterValue?.orderDir
												? ''
												: val
										)
									}
									options={_orderByOptions}
								/>
							</Col>
						)}

						{!hide.includes('deleted') && (
							<Col id="target-filter-deleted">
								<Checkbox
									checked={uiStore?.del || false}
									onChange={(e) =>
										uiStore.setDel(e.target.checked)
									}
								>
									{translate('single.deleted')}
								</Checkbox>{' '}
							</Col>
						)}
					</div>
				</Row>
			</BrowserView>

			{/* Display filter for mobile devices */}
			<MobileView>
				<Row
					className="we-filter"
					id="target-filter"
					align="middle"
					gutter={[10, 10]}
					style={{
						width: '100%',
						borderRadius: 4
					}}
				>
					{/* First Row */}
					{!hide.includes('search') && (
						<Col id="target-filter-search">
							<Search
								defaultValue={uiStore.search || ''}
								placeholder={t('single.search.label')}
								onSearch={(val) => uiStore.setSearch(val)}
								enterButton
								allowClear
							/>
						</Col>
					)}

					{/* Filters only for notification*/}
					{isForNotification && (
						<>
							{!hide.includes('entity_table') && (
								<Col id="target-filter-entity_table">
									<OptionsSelector
										value={filterValue?.entity_table || ''}
										onChange={(val) =>
											uiStore.setFilter(
												'entity_table',
												val
											)
										}
										options={_entityTableOptions}
									/>
								</Col>
							)}
						</>
					)}

					{/* Second Row - Hidden by Default */}
					<Col
						className={`filter-advanced ${
							uiStore.filterExpanded ? 'visible' : 'hidden'
						}`}
						style={{ width: '100%' }}
					>
						<Row
							gutter={[10, 10]}
							style={{ width: '100%' }}
							align="middle"
						>
							{/* Filters only for documentation starts*/}
							{isForDocumentation && (
								<Col id="target-filter-appType">
									{!hide.includes('appType') && (
										<OptionsSelector
											value={filterValue?.appType || ''}
											onChange={(val) =>
												uiStore.setFilter(
													'appType',
													val
												)
											}
											options={_appTypeOptions}
										/>
									)}
								</Col>
							)}
							{/* Filters only for documentation ends*/}

							{/* Filters only for notification starts*/}
							{isForNotification && (
								<>
									<Col
										span={12}
										id="target-filter-entity_table"
									>
										{!hide.includes('entity_table') && (
											<OptionsSelector
												dropdownMinWidth="100px"
												value={
													filterValue?.entity_table ||
													''
												}
												onChange={(val) =>
													uiStore.setFilter(
														'entity_table',
														val
													)
												}
												options={_entityTableOptions}
											/>
										)}
									</Col>
									<Col span={12} id="target-filter-kind">
										{!hide.includes('kind') && (
											<OptionsSelector
												dropdownMinWidth="100px"
												value={filterValue?.kind || ''}
												onChange={(val) =>
													uiStore.setFilter(
														'kind',
														val
													)
												}
												options={_kindOptions}
											/>
										)}
									</Col>
									<Col id="target-filter-show_all">
										{!hide.includes('show_all') &&
											isSuperAdmin && (
												<Checkbox
													checked={
														filterValue?.show_all ||
														false
													}
													onChange={(e) =>
														uiStore.setFilter(
															'show_all',
															e.target.checked
														)
													}
												>
													{translate(
														'single.show-all'
													)}
												</Checkbox>
											)}
									</Col>
								</>
							)}
							{/* Filters only for notification ends*/}

							{/* Filters only for budget starts*/}
							{isForBudget && (
								<>
									<Col span={24} id="target-filter-currency">
										{!hide.includes('currency') && (
											<OptionsSelector
												dropdownMinWidth="200px"
												value={
													filterValue?.currency || ''
												}
												onChange={(val) =>
													uiStore.setFilter(
														'currency',
														val
													)
												}
												options={_currencyOptions}
											/>
										)}
									</Col>
									<Col span={24} id="target-filter-for-user">
										<OptionsSelector
											value={
												filterValue?.assigned_user_id ||
												''
											}
											onChange={(val) =>
												uiStore.setFilter(
													'assigned_user_id',
													val
												)
											}
											options={[
												{
													key: 'assigned-user',
													value: '',
													label: translate(
														'__assigned%_user'
													)
												},
												..._userOptions
											]}
										/>
									</Col>
								</>
							)}
							{/* Filters only for budget ends*/}

							{!hide.includes('progress') && (
								<Col span={12} id="target-filter-progress">
									<OptionsSelector
										dropdownMinWidth="100px"
										value={filterValue?.progress || ''}
										onChange={(val) =>
											uiStore.setFilter('progress', val)
										}
										options={_progressOptions}
									/>
								</Col>
							)}
							{!hide.includes('priority') && (
								<Col span={12} id="target-filter-by-priority">
									<OptionsSelector
										dropdownMinWidth="100px"
										value={filterValue?.priority || ''}
										onChange={(val) =>
											uiStore.setFilter('priority', val)
										}
										options={_priorityOptions}
									/>
								</Col>
							)}
							{!hide.includes('category') && (
								<Col span={12} id="target-filter-category">
									<AllowedCategories
										dropdownMinWidth="100px"
										value={filterValue?.category || ''}
										onChange={(val) =>
											uiStore.setFilter('category', val)
										}
										optionStart={{
											key: 'by-category',
											value: '',
											label: translate('__select.item%')
										}}
									/>
								</Col>
							)}
							{!hide.includes('by-user') && (
								<Col span={12} id="target-filter-user">
									<OptionsSelector
										dropdownMinWidth="100px"
										value={filterValue?.user_uid || ''}
										onChange={(val) =>
											uiStore.setFilter('user_uid', val)
										}
										options={[
											{
												key: 'by-user',
												value: '',
												label: translate(
													'__select.item%_user.single'
												)
											},
											..._userOptions
										]}
									/>
								</Col>
							)}
							{!hide.includes('for-user') && (
								<Col span={12} id="target-filter-for-user">
									<OptionsSelector
										dropdownMinWidth="100px"
										value={filterValue?.for_user_uid || ''}
										onChange={(val) =>
											uiStore.setFilter(
												'for_user_uid',
												val
											)
										}
										options={[
											{
												key: 'for-user',
												value: '',
												label: translate(
													'single.for-user.label'
												)
											},
											..._userOptions
										]}
									/>
								</Col>
							)}

							{!hide.includes('orderBy') && (
								<Col span={12} id="target-filter-orderBy">
									<FilterOrderBy
										dropdownMinWidth="250px"
										value={filterValue?.orderBy || ''}
										orderDir={filterValue?.orderDir || ''}
										onChange={(val) =>
											uiStore.setFilter('orderBy', val)
										}
										onOrderDirChange={(val) =>
											uiStore.setFilter(
												'orderDir',
												val === filterValue?.orderDir
													? ''
													: val
											)
										}
										options={_orderByOptions}
									/>
								</Col>
							)}
							{addNew && !hide.includes('addNew') && (
								<Col span={12}>
									<Button
										style={{ width: '100%' }}
										id="target-filter-addnew"
										onClick={addNew}
									>
										{t('__add')}
									</Button>
								</Col>
							)}
							{!hide.includes('deleted') && (
								<Col span={12} id="target-filter-deleted">
									<Checkbox
										checked={uiStore?.del || false}
										onChange={(e) =>
											uiStore.setDel(e.target.checked)
										}
									>
										{translate('single.deleted')}
									</Checkbox>
								</Col>
							)}
						</Row>
					</Col>

					{/* Expand/Collapse Button */}
					<Col span={uiStore.filterExpanded && 24}>
						<Button
							onClick={() => uiStore.setFilterExpanded()}
							style={{
								width: uiStore.filterExpanded ? '100%' : 'auto'
							}}
						>
							{uiStore.filterExpanded ? (
								<span>
									<UpOutlined /> More Filters
								</span>
							) : (
								<span>
									<DownOutlined />
								</span>
							)}
						</Button>
					</Col>
				</Row>
			</MobileView>
		</ConfigProvider>
	)
}

Filter.propTypes = {
	uiStore: PropTypes.object.isRequired,
	addNew: PropTypes.func,
	progressOptions: PropTypes.array,
	priorityOptions: PropTypes.array,
	userOptions: PropTypes.array,
	orderByOptions: PropTypes.array,
	entityTableOptions: PropTypes.array,
	currencyOptions: PropTypes.array,
	kindOptions: PropTypes.array,
	appTypeOptions: PropTypes.array,
	hide: PropTypes.array,
	isForNotification: PropTypes.bool,
	isForDocumentation: PropTypes.bool,
	isForUser: PropTypes.bool,
	isForBudget: PropTypes.bool,
	module: PropTypes.string
}

export default observer(Filter)

/**
 * FilterOrderBy component allows users to select filter options and specify
 * the order direction for sorting. It combines a dropdown selection with
 * ascending/descending order checkboxes for a user-friendly filtering interface.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Array} props.options - An array of options to display in the dropdown.
 * @param {string|number|Object|Array} props.value - The current selected value.
 * @param {Function} props.onChange - Callback function to handle value changes.
 * @param {string} props.orderDir - The current order direction ('asc' or 'desc').
 * @param {Function} props.onOrderDirChange - Callback function to handle order direction changes.
 * @returns {JSX.Element} The rendered FilterOrderBy component.
 */
const FilterOrderBy = ({
	options,
	value,
	onChange,
	orderDir,
	onOrderDirChange,
	...props
}) => {
	const {
		style,
		className,
		placeholder,
		listHeight,
		listItemHeight,
		dropdownStyle,
		dropdownMinWidth = false,
		...rest
	} = props

	const minWidth = dropdownMinWidth
		? { minWidth: dropdownMinWidth }
		: { minWidth: '200px' }

	return (
		<div
			style={{ display: 'flex', alignItems: 'center' }}
			className="we-order-by"
		>
			<Select
				allowClear
				options={options}
				value={value}
				onChange={onChange}
				placement="bottomLeft"
				placeholder={placeholder ?? '--'}
				className={className ?? 'antd-select-style-1 '}
				listHeight={listHeight ?? 150}
				listItemHeight={listItemHeight ?? 10}
				style={{
					...{ width: '100%', display: 'inline-block' },
					...style,
					...minWidth
				}}
				dropdownStyle={{ ...minWidth, ...dropdownStyle }}
				// Render container for order direction checkboxes
				dropdownRender={(menu) => (
					<>
						<div
							style={{
								display: 'flex',
								backgroundColor: 'white',
								borderRadius: '4px',
								boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
								padding: '8px',
								marginBottom: '8px'
							}}
						>
							<Checkbox
								checked={orderDir === 'asc'}
								onChange={() => onOrderDirChange('asc')}
							>
								{translate('single.ascending')}
							</Checkbox>
							<Checkbox
								checked={orderDir === 'desc'}
								onChange={() => onOrderDirChange('desc')}
							>
								{translate('single.descending')}
							</Checkbox>
						</div>
						<div>{menu}</div>
					</>
				)}
				{...rest}
			/>
		</div>
	)
}

FilterOrderBy.propTypes = {
	options: PropTypes.array,
	value: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.object,
		PropTypes.array,
		PropTypes.number
	]),
	orderDir: PropTypes.string,
	onChange: PropTypes.func,
	onOrderDirChange: PropTypes.func,
	style: PropTypes.object,
	className: PropTypes.string,
	placeholder: PropTypes.string,
	listHeight: PropTypes.number,
	listItemHeight: PropTypes.number,
	dropdownStyle: PropTypes.object,
	dropdownMinWidth: PropTypes.string
}
